import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import Logo from "@/components/Logo";
import { trackCta } from "@/lib/analytics";
import { WhatsAppCta } from "@/components/WhatsAppContact";
import { useT } from "@/i18n/LanguageContext";

export default function Footer() {
  const year = new Date().getFullYear();
  const { t } = useT();
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/5 bg-ink mt-10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Logo />
          <p className="mt-6 max-w-sm text-fog font-light leading-relaxed">
            {t("footer.tagline")}
          </p>
          <p className="mt-8 text-xs tracking-[0.25em] uppercase text-gold/80">{t("footer.knowThyself")}</p>
          <Link
            to="/#newsletter"
            data-testid="footer-newsletter-link"
            onClick={() => trackCta("newsletter_open", { placement: "footer" })}
            className="mt-6 inline-block text-sm text-gold/80 hover:text-gold underline underline-offset-4 decoration-gold/40 transition-colors"
          >
            {t("newsletter.cta.button")}
          </Link>
          <div className="mt-8">
            <WhatsAppCta source="footer" />
            <p className="mt-3 max-w-sm text-xs text-fog/60 leading-relaxed">
              Questions about Virtue Guide, the Academy, membership or your account? Message us directly on WhatsApp.
            </p>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-[11px] tracking-[0.25em] uppercase text-gold mb-5">{t("footer.company")}</h4>
          <ul className="space-y-3 text-fog">
            <li><Link to="/agora" data-testid="footer-link-agora" className="hover:text-bone transition-colors">{t("nav.agora")}</Link></li>
            <li><Link to="/stoic-journal" data-testid="footer-link-journal" className="hover:text-bone transition-colors">{t("nav.journal")}</Link></li>
            <li><Link to="/stoic-training-cards" data-testid="footer-link-cards" className="hover:text-bone transition-colors">{t("nav.cards")}</Link></li>
            <li><Link to="/ancient-library" data-testid="footer-link-ancient-library" className="hover:text-bone transition-colors">{t("nav.ancientLibrary")}</Link></li>
            <li><Link to="/my-insights" data-testid="footer-link-insights" className="hover:text-bone transition-colors">{t("nav.insights")}</Link></li>
            <li><Link to="/my-books" data-testid="footer-link-books" className="hover:text-bone transition-colors">{t("nav.books")}</Link></li>
            <li><Link to="/about" data-testid="footer-link-about" className="hover:text-bone transition-colors">{t("footer.aboutLink")}</Link></li>
            <li><Link to="/faq" data-testid="footer-link-faq" className="hover:text-bone transition-colors">FAQ</Link></li>
            <li><Link to="/support" data-testid="footer-link-support" className="hover:text-bone transition-colors">{t("nav.support")}</Link></li>
            <li><Link to="/terms" data-testid="footer-link-terms" className="hover:text-bone transition-colors">{t("footer.termsLink")}</Link></li>
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-bone transition-colors">{t("support.privacyValue")}</Link></li>
            <li>
              <Link
                to="/admin"
                data-testid="footer-link-founder-portal"
                className="inline-flex items-center gap-1.5 text-fog/40 hover:text-gold/70 transition-colors text-xs tracking-wider"
              >
                <Lock size={11} strokeWidth={1.5} />
                Founder Portal
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-[11px] tracking-[0.25em] uppercase text-gold mb-5">{t("footer.product")}</h4>
          <ul className="space-y-3 text-fog">
            <li><Link to="/ai-stoic-coach" data-testid="footer-link-ai-stoic-coach" className="hover:text-bone transition-colors">AI Stoic Coach</Link></li>
            <li><Link to="/wisdom-coach" data-testid="footer-link-wisdom-coach" className="hover:text-bone transition-colors">Wisdom Coach</Link></li>
            <li><Link to="/self-reflection-app" data-testid="footer-link-self-reflection" className="hover:text-bone transition-colors">Self-Reflection App</Link></li>
            <li><Link to="/marcus-aurelius-ai" data-testid="footer-link-marcus" className="hover:text-bone transition-colors">Marcus Aurelius AI</Link></li>
            <li><Link to="/seneca-ai" data-testid="footer-link-seneca" className="hover:text-bone transition-colors">Seneca AI</Link></li>
            <li><Link to="/epictetus-ai" data-testid="footer-link-epictetus" className="hover:text-bone transition-colors">Epictetus AI</Link></li>
            <li><a href="mailto:ek@stoicos.ai" onClick={() => trackCta("contact_email", { placement: "footer" })} data-testid="footer-link-email" className="hover:text-bone transition-colors">ek@stoicos.ai</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-fog/80">
          <span>© {year} StoicOs. {t("footer.rights")}</span>
          <span className="font-serif italic text-fog/70">Tu ne quaesieris, scire nefas, quem mihi, quem tibi finem di dederint.</span>
        </div>
      </div>
    </footer>
  );
}
